import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Code,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { ArrowClockwise, CaretDown, CaretRight, FunnelSimple } from 'phosphor-react';

import { IApiAccessLog, IApiToken, useApiController } from '../../../hooks/useApiController';
import { IDataNoPagination } from '../../../types/shared';
import { colors } from '../../../theme/colors';

function statusColor(status?: number | null): string {
  if (!status) return 'gray';
  if (status >= 200 && status < 300) return 'green';
  if (status === 429 || status === 423) return 'orange';
  return 'red';
}

const COLUNAS_TABELA = 9; // qtd de <Th> da tabela — mantém o colSpan do detalhe em sincronia

// Empresas de um log: as N do token (multi-empresa) ou o external_id singular (legado).
function empresasDoLog(log: IApiAccessLog): number[] {
  if (log.external_ids && log.external_ids.length > 0) return log.external_ids;
  if (log.external_id != null) return [log.external_id];
  return [];
}

// Mostra as empresas da requisição: a 1ª + "+N" quando há várias (multi-empresa).
// No hover, um tooltip lista todas. Cai no external_id singular para logs antigos.
function EmpresasCell({ log }: { log: IApiAccessLog }) {
  const ids = empresasDoLog(log);

  if (ids.length === 0) return <Text fontSize="sm">-</Text>;
  if (ids.length === 1) return <Text fontSize="sm">{`Empresa ${ids[0]}`}</Text>;

  const listaCompleta = ids.map(id => `Empresa ${id}`).join(', ');
  return (
    <Tooltip label={listaCompleta} hasArrow placement="top" openDelay={200}>
      <Flex align="center" gap={1} cursor="default" display="inline-flex">
        <Text fontSize="sm">{`Empresa ${ids[0]}`}</Text>
        <Badge colorScheme="blue" fontSize="0.7em">{`+${ids.length - 1}`}</Badge>
      </Flex>
    </Tooltip>
  );
}

function AuditoriaRow({
  log,
  tokenNome,
  clienteNome,
}: {
  log: IApiAccessLog;
  tokenNome?: string;
  clienteNome?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const empresas = empresasDoLog(log);
  // expande se há metadados OU várias empresas (p/ ver a lista completa)
  const temDetalhe = !!log.metadados_request || empresas.length > 1;
  const userAgent = (log.metadados_request?.user_agent as string | undefined) ?? undefined;
  const navegador = (log.metadados_request?.navegador as string | undefined) ?? undefined;
  const sistemaOperacional =
    (log.metadados_request?.sistema_operacional as string | undefined) ?? undefined;

  return (
    <>
      <Tr _hover={{ bg: 'gray.50' }}>
        <Td px={2}>
          {temDetalhe && (
            <IconButton
              aria-label="expandir"
              size="xs"
              variant="ghost"
              icon={aberto ? <CaretDown /> : <CaretRight />}
              onClick={() => setAberto(v => !v)}
            />
          )}
        </Td>
        <Td>
          <Text fontSize="sm" color="gray.600">
            {log.acessado_em ? parseUTC(log.acessado_em).toLocaleString('pt-BR') : '-'}
          </Text>
        </Td>
        <Td>
          {/* nome ATUAL do token (resolvido por join com a lista de tokens), não o id cru */}
          <Text fontSize="sm">
            {tokenNome ?? (log.api_token_id ? `Token ${log.api_token_id}` : '-')}
          </Text>
          {clienteNome && (
            <Text fontSize="xs" color="gray.400">
              {clienteNome}
            </Text>
          )}
        </Td>
        <Td>
          <Text fontSize="sm">{log.recurso ?? '-'}</Text>
        </Td>
        <Td>
          <EmpresasCell log={log} />
        </Td>
        <Td>
          <Code fontSize="xs">{`${log.metodo ?? ''} ${log.uri ?? ''}`}</Code>
        </Td>
        <Td>
          <Badge colorScheme={statusColor(log.status_http)}>{log.status_http ?? '-'}</Badge>
        </Td>
        <Td isNumeric>
          <Text fontSize="sm">{log.linhas_retornadas ?? '-'}</Text>
        </Td>
        <Td>
          <Text fontSize="sm">{log.ip_hash ?? '-'}</Text>
          {(navegador || sistemaOperacional) && (
            <Text fontSize="xs" color="gray.400">
              {[navegador, sistemaOperacional].filter(Boolean).join(' · ')}
            </Text>
          )}
        </Td>
      </Tr>
      {temDetalhe && (
        <Tr>
          <Td colSpan={COLUNAS_TABELA} p={0} border="none">
            <Collapse in={aberto} animateOpacity>
              <Box bg="gray.50" p={3} borderRadius="md" m={2}>
                {empresas.length > 1 && (
                  <Box mb={log.metadados_request ? 3 : 0}>
                    <Text fontSize="xs" fontWeight="medium" mb={1} color="gray.500">
                      Empresas da requisição ({empresas.length})
                    </Text>
                    <Flex gap={1} flexWrap="wrap">
                      {empresas.map(id => (
                        <Badge key={id} colorScheme="blue">{`Empresa ${id}`}</Badge>
                      ))}
                    </Flex>
                  </Box>
                )}
                {log.metadados_request && (
                  <>
                    <Text fontSize="xs" fontWeight="medium" mb={1} color="gray.500">
                      Metadados do request (sem dados sensíveis)
                    </Text>
                    {userAgent && (
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        User-Agent: <Code fontSize="xs">{userAgent}</Code>
                      </Text>
                    )}
                    <Code display="block" whiteSpace="pre-wrap" fontSize="xs" p={2} bg="white">
                      {JSON.stringify(log.metadados_request, null, 2)}
                    </Code>
                  </>
                )}
              </Box>
            </Collapse>
          </Td>
        </Tr>
      )}
    </>
  );
}

const FILTRO_VAZIO = {
  de: '',
  ate: '',
  tokenId: '' as number | '',
  status: '',
  usuarioId: '' as number | '',
  externalId: '' as number | '',
};

const POLLING_MS = 15000; // recarrega a cada 15s quando o auto-refresh está ligado

// O backend grava os timestamps em UTC e serializa SEM o sufixo "Z" (datetime.utcnow
// naive → isoformat). new Date() cru interpretaria como hora LOCAL → off-by-3h. Este
// helper força a leitura como UTC (regra do projeto: comparar/exibir tratando a origem
// como UTC-0). Se a string já vier com Z/offset, respeita.
function parseUTC(iso: string): Date {
  const temTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(temTz ? iso : `${iso}Z`);
}

export function AuditoriaTab() {
  const { getLogs, getTokens } = useApiController();
  const [filtro, setFiltro] = useState(FILTRO_VAZIO);
  // auto-refresh (polling) — ligado por padrão; o admin pode desligar no toggle.
  const [autoRefresh, setAutoRefresh] = useState(true);

  const { data, isFetching, isError, refetch, dataUpdatedAt } = useQuery<
    IDataNoPagination<IApiAccessLog[]>
  >({
    queryKey: ['api-controller-logs', {}],
    queryFn: getLogs,
    refetchOnWindowFocus: false,
    refetchInterval: autoRefresh ? POLLING_MS : false,
  });
  // tokens: p/ cruzar token→cliente/empresa nos filtros e no cabeçalho (client-side)
  const { data: tokensData } = useQuery<IDataNoPagination<IApiToken[]>>({
    queryKey: ['api-controller-tokens', {}],
    queryFn: getTokens,
  });

  const logs = data?.data ?? [];
  const tokens = tokensData?.data ?? [];
  const tokenById = useMemo(() => new Map(tokens.map(t => [t.id, t])), [tokens]);

  // opções de status e empresa a partir dos logs (só o que existe)
  // sort com comparador numérico: status_http é number; o .sort() padrão ordena
  // como string (ex.: 429 antes de 5) — o comparador garante ordem numérica.
  const statusOptions = useMemo(
    () =>
      Array.from(new Set(logs.map(l => l.status_http).filter(Boolean))).sort(
        (a, b) => (a as number) - (b as number),
      ),
    [logs],
  );

  // Opções de empresa: achata TODAS as empresas de TODOS os logs (multi-empresa),
  // com fallback ao external_id singular dos logs antigos. Ordenado numericamente.
  const empresasDosLogs = useMemo(() => {
    const set = new Set<number>();
    logs.forEach(l => empresasDoLog(l).forEach(id => set.add(id)));
    return Array.from(set).sort((a, b) => a - b);
  }, [logs]);

  // Filtragem 100% client-side (o backend não filtra — decisão de produto).
  // Os limites vêm do <input datetime-local> (hora local do admin) → new Date() os lê
  // como local; o acessado_em vem em UTC → parseUTC. Ambos viram instante absoluto (ms),
  // então a comparação é correta independentemente do fuso.
  const logsFiltrados = useMemo(() => {
    const deTs = filtro.de ? new Date(filtro.de).getTime() : null;
    const ateTs = filtro.ate ? new Date(filtro.ate).getTime() : null;
    return logs.filter(l => {
      if (filtro.tokenId !== '' && l.api_token_id !== filtro.tokenId) return false;
      if (filtro.status !== '' && String(l.status_http) !== filtro.status) return false;
      if (filtro.externalId !== '') {
        // casa se a empresa filtrada está entre as empresas do log (multi-empresa)
        if (!empresasDoLog(l).includes(filtro.externalId as number)) return false;
      }
      if (filtro.usuarioId !== '') {
        const tk = l.api_token_id ? tokenById.get(l.api_token_id) : undefined;
        if (!tk || tk.usuario_id !== filtro.usuarioId) return false;
      }
      if (deTs || ateTs) {
        const ts = l.acessado_em ? parseUTC(l.acessado_em).getTime() : 0;
        if (deTs && ts < deTs) return false;
        if (ateTs && ts > ateTs) return false;
      }
      return true;
    });
  }, [logs, filtro, tokenById]);

  const set = (campo: keyof typeof FILTRO_VAZIO, valor: string | number) =>
    setFiltro(prev => ({ ...prev, [campo]: valor }));

  if (isError) {
    return (
      <Flex direction="column" align="center" gap={2} py={10}>
        <Text fontWeight="medium" color="red.500">
          Falha ao carregar a auditoria
        </Text>
        <Button size="sm" leftIcon={<ArrowClockwise weight="bold" />} onClick={() => refetch()}>
          Recarregar
        </Button>
      </Flex>
    );
  }

  if (isFetching && logs.length === 0) {
    return (
      <Flex justify="center" py={10}>
        <Spinner size="lg" color="primary" />
      </Flex>
    );
  }

  const ultimaAtualizacao = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('pt-BR')
    : '—';

  return (
    <Box>
      {/* ── Controles: recarregar + auto-refresh (polling) ─────────────────── */}
      <Flex align="center" justify="space-between" mb={3} gap={3} flexWrap="wrap">
        <Flex align="center" gap={2}>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<ArrowClockwise weight="bold" />}
            isLoading={isFetching}
            onClick={() => refetch()}
          >
            Recarregar
          </Button>
          <Text fontSize="xs" color="gray.500">
            Atualizado às {ultimaAtualizacao}
          </Text>
        </Flex>
        <Flex align="center" gap={2}>
          <FormControl display="flex" alignItems="center" gap={2} w="auto">
            <Switch
              size="sm"
              isChecked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
            />
            <FormLabel mb={0} fontSize="sm">
              Auto-atualizar {autoRefresh ? `(a cada ${POLLING_MS / 1000}s)` : '(desligado)'}
            </FormLabel>
          </FormControl>
        </Flex>
      </Flex>

      {/* ── Filtros (client-side) ─────────────────────────────────────────── */}
      <Box borderWidth="1px" borderRadius="md" p={3} mb={3}>
        <Flex align="center" gap={2} mb={2}>
          <FunnelSimple size={16} />
          <Text fontSize="sm" fontWeight="medium">
            Filtros
          </Text>
        </Flex>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="xs">De</FormLabel>
            <Input
              size="sm"
              type="datetime-local"
              value={filtro.de}
              onChange={e => set('de', e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs">Até</FormLabel>
            <Input
              size="sm"
              type="datetime-local"
              value={filtro.ate}
              onChange={e => set('ate', e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs">Token</FormLabel>
            <Select
              size="sm"
              placeholder="Todos"
              value={filtro.tokenId}
              onChange={e => set('tokenId', e.target.value ? Number(e.target.value) : '')}
            >
              {tokens.map(t => (
                <option key={t.id} value={t.id}>
                  {t.nome} (#{t.id})
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs">Cliente</FormLabel>
            <Select
              size="sm"
              placeholder="Todos"
              value={filtro.usuarioId}
              onChange={e => set('usuarioId', e.target.value ? Number(e.target.value) : '')}
            >
              {Array.from(
                new Map(
                  tokens
                    .filter(t => t.usuario_id)
                    .map(t => [t.usuario_id, t.usuario_nome ?? `Usuário ${t.usuario_id}`]),
                ).entries(),
              ).map(([uid, nome]) => (
                <option key={uid} value={uid as number}>
                  {nome}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs">Empresa</FormLabel>
            <Select
              size="sm"
              placeholder="Todas"
              value={filtro.externalId}
              onChange={e => set('externalId', e.target.value ? Number(e.target.value) : '')}
            >
              {empresasDosLogs.map(eid => (
                <option key={eid} value={eid}>
                  Empresa {eid}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs">Status</FormLabel>
            <Select
              size="sm"
              placeholder="Todos"
              value={filtro.status}
              onChange={e => set('status', e.target.value)}
            >
              {statusOptions.map(s => (
                <option key={s} value={String(s)}>
                  {s}
                </option>
              ))}
            </Select>
          </FormControl>
        </SimpleGrid>
        <Flex justify="flex-end" mt={2}>
          <Button size="xs" variant="ghost" onClick={() => setFiltro(FILTRO_VAZIO)}>
            Limpar filtros
          </Button>
        </Flex>
      </Box>

      {logsFiltrados.length === 0 ? (
        <Flex direction="column" align="center" gap={2} py={10}>
          <CaretRight size={64} color={colors.primary} />
          <Text fontWeight="medium">Nenhuma requisição encontrada</Text>
          <Text color="gray.500" fontSize="sm">
            {logs.length === 0
              ? 'As chamadas à API pública aparecerão aqui.'
              : 'Ajuste os filtros para ver as requisições.'}
          </Text>
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th px={2} />
                <Th>Quando</Th>
                <Th>Token / Cliente</Th>
                <Th>Recurso</Th>
                <Th>Empresa</Th>
                <Th>Requisição</Th>
                <Th>Status</Th>
                <Th isNumeric>Linhas</Th>
                <Th>IP / Dispositivo</Th>
              </Tr>
            </Thead>
            <Tbody>
              {logsFiltrados.map(log => (
                <AuditoriaRow
                  key={log.id}
                  log={log}
                  tokenNome={
                    log.api_token_id
                      ? (tokenById.get(log.api_token_id)?.nome ?? undefined)
                      : undefined
                  }
                  clienteNome={
                    log.api_token_id
                      ? (tokenById.get(log.api_token_id)?.usuario_nome ?? undefined)
                      : undefined
                  }
                />
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
